<template>
    <div class="ask-answer">
        <!-- The answer's own names are the links. `v-html` of our own render,
             through `html:false` markdown-it, so nothing the model wrote is
             ever interpreted as markup. eslint-disable-next-line vue/no-v-html -->
        <div
            class="ask-answer__prose"
            data-testid="ask-answer-prose"
            @click="onClick"
            v-html="rendered.html"
        />

        <AskReferences
            v-if="unlinked.length"
            :references="unlinked"
            @navigate="emit('navigate')"
        />
    </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";

import AskReferences from "@/components/AskBox/AskReferences.vue";
import {
    linkReferences,
    referenceKey,
    renderAnswer,
} from "@/components/AskBox/ask-content";
import { entityTypeLabel } from "@/components/EntityInfo/entity-type-label";
import { entityIcon } from "@/components/Thread/reconcile/entity-icons";
import type { AskReference } from "@/lib/openapi/gen/types.gen";
import { withThreadRevision } from "@/lib/thread-revision-link";
import { entityLocation } from "@/router/entity-location";

/**
 * One answer, with the things it names as links and a chip for anything it
 * did not name.
 *
 * Before this the prose and the chip row said the same things twice: an answer
 * about seven processes named all seven in a sentence and then showed the same
 * seven as chips underneath, and neither was easy to scan. Now the name in the
 * prose *is* the link — built from the reference the backend already checked
 * against the stek — and the row underneath carries only what the prose left
 * unnamed. Both the live answer and every earlier one render through here, so
 * the two cannot drift.
 *
 * Its own component for the reason `AskReferences` is: it needs the router and
 * `withThreadRevision` reaches the release store, and `AskBox.spec.ts` mounts
 * without either. The text work itself is in `linkReferences`, which is pure
 * and tested on its own.
 *
 * Clicks are handled here, by delegation, rather than by turning each anchor
 * into a `RouterLink` — `v-html` cannot hold components. Only an unmodified
 * left click is taken over; a ctrl- or middle-click keeps the real `href`
 * and opens a tab, exactly as `AskReferences` reasons with `@click.exact`.
 */
const props = defineProps<{
    content: string;
    references: AskReference[];
}>();

const emit = defineEmits<{ navigate: [] }>();

const route = useRoute();
const router = useRouter();

const params = computed(() => ({
    orgName: route.params.orgName,
    stekName: route.params.stekName,
}));

const locate = (reference: { entity_type: string; entity_id: string }) =>
    entityLocation(reference, params.value);

const rendered = computed(() =>
    linkReferences(renderAnswer(props.content), props.references, {
        locate,
        href: (to) => router.resolve(withThreadRevision(to)).href,
        icon: (reference) => entityIcon(reference.entity_type),
        title: (reference) =>
            entityTypeLabel(reference.entity_type) || undefined,
    })
);

/** The references the prose did not name — the only ones that need a chip. */
const unlinked = computed(() =>
    props.references.filter(
        (reference) => !rendered.value.linked.has(referenceKey(reference))
    )
);

const onClick = (event: MouseEvent) => {
    if (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
    ) {
        return;
    }
    const anchor = (event.target as Element | null)?.closest?.(
        "a[data-entity-id]"
    ) as HTMLAnchorElement | null;
    if (!anchor) {
        return;
    }
    const to = locate({
        entity_type: anchor.dataset.entityType ?? "",
        entity_id: anchor.dataset.entityId ?? "",
    });
    if (!to) {
        return;
    }
    event.preventDefault();
    void router.push(withThreadRevision(to));
    emit("navigate");
};
</script>

<style scoped>
.ask-answer {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.ask-answer__prose {
    font-size: 0.95rem;
    line-height: var(--body-line-height, 1.5);
}

.ask-answer__prose :deep(p) {
    margin: 0.5em 0;
}

.ask-answer__prose :deep(p:first-child) {
    margin-top: 0;
}

.ask-answer__prose :deep(p:last-child) {
    margin-bottom: 0;
}

/* A URL the model wrote out leaves the app, and looks like it: the ordinary
   blue underlined link. */
.ask-answer__prose :deep(a) {
    color: var(--p-primary-color);
    text-decoration: underline;
    overflow-wrap: anywhere;
}

/* A name the answer linked does not leave the app — it goes to a thing in
   this stek — so it is drawn the way Notion, Linear and Slack draw a mention
   rather than the way the web draws a hyperlink: the type's icon, the name in
   the text's own colour, a quiet ground behind both so they read as one
   token. Seven blue underlined lines in a row read as a list of links, not as
   a list of processes; seven of these read as names. Colour arrives on hover,
   as it does on the chips, so the two stay one visual language. */
.ask-answer__prose :deep(a.ask-answer__link) {
    color: inherit;
    font-weight: 500;
    text-decoration: none;
    background: var(--p-surface-100);
    border-radius: 0.3em;
    padding: 0.05em 0.35em;
    /* A long name that wraps keeps its ground on both lines. Prefixed as well
       because nothing in the build adds prefixes, and Safari still wants it. */
    -webkit-box-decoration-break: clone;
    box-decoration-break: clone;
    transition:
        color 0.15s,
        background-color 0.15s;
}

.ask-answer__prose :deep(a.ask-answer__link:hover) {
    color: var(--p-primary-color);
    background: var(--p-surface-200);
}

.ask-answer__prose :deep(a.ask-answer__link i) {
    font-size: 0.75em;
    margin-right: 0.35em;
    opacity: 0.7;
    vertical-align: 0.05em;
}

.ask-answer__prose :deep(code) {
    background: var(--p-surface-200);
    padding: 0.1em 0.3em;
    font-size: 0.85em;
}

/* A list is how an answer about seven things is meant to read now, so the
   items get room to be scanned rather than the tight spacing of a footnote.
   The marker is set explicitly because the app's reset strips it: without
   this the seven lines rendered indented but unmarked, and read as a broken
   paragraph rather than a list — seen in the browser, not deduced. */
.ask-answer__prose :deep(ul),
.ask-answer__prose :deep(ol) {
    margin: 0.5em 0;
    padding-left: 1.4em;
    list-style-position: outside;
}

.ask-answer__prose :deep(ul) {
    list-style-type: disc;
}

.ask-answer__prose :deep(ol) {
    list-style-type: decimal;
}

.ask-answer__prose :deep(li) {
    margin: 0.2em 0;
}
</style>
